import React, { useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { API_URL, FRONTEND_URL } from '@/lib/consts';

import { DisplayConceptRef } from '../lib/api';
import { RemoveTopicButton } from './RemoveButton';
import { TopicSelector } from './TopicSelector';

interface InviteFormProps {
  // Context related props
  threadContext?: string;

  // Optional message to display above form
  introMessage?: React.ReactNode;

  // Callback props
  onInviteCreated?: (inviteCode: string, inviteUrl: string) => void;
  onCancel?: () => void;
}

interface InviteFormData {
  inviterTopics: string[];
  inviteeTopics: string[];
  nickname: string;
  threadContext?: string;
}

export const InviteForm: React.FC<InviteFormProps> = ({
  threadContext,
  introMessage,
  onInviteCreated,
  onCancel,
}) => {
  const [inviterTopics, setInviterTopics] = useState<DisplayConceptRef[]>([]);
  const [inviteeTopics, setInviteeTopics] = useState<DisplayConceptRef[]>([]);
  const [inviteCode, setInviteCode] = useState<string>('');
  const [nickname, setNickname] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData: InviteFormData = {
        inviterTopics: inviterTopics.map((topic) => topic.cref),
        inviteeTopics: inviteeTopics.map((topic) => topic.cref),
        nickname,
      };

      // Add threadContext if available
      if (threadContext) {
        formData.threadContext = threadContext;
      }

      const response = await fetch(`${API_URL}/auth/create_invite`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to generate invite code');
      }

      const data = await response.json();
      setInviteCode(data.inviteCode);

      const inviteUrl = `${FRONTEND_URL}/register/${data.inviteCode}`;

      // Call the callback with the invite code and URL if provided
      if (onInviteCreated) {
        onInviteCreated(data.inviteCode, inviteUrl);
      }
    } catch (error) {
      console.error('Error generating invite code:', error);
      setError('Failed to generate invite code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTopicSelect = (topic: DisplayConceptRef | null, isFollowTopics: boolean) => {
    if (!topic) {
      return;
    }
    if (isFollowTopics) {
      setInviterTopics((prevTopics) => [...prevTopics, topic]);
    } else {
      setInviteeTopics((prevTopics) => [...prevTopics, topic]);
    }
  };

  const removeTopic = (index: number, isFollowTopics: boolean) => {
    if (isFollowTopics) {
      setInviterTopics((prevTopics) => prevTopics.filter((_, i) => i !== index));
    } else {
      setInviteeTopics((prevTopics) => prevTopics.filter((_, i) => i !== index));
    }
  };

  const inviteUrl = inviteCode ? `${FRONTEND_URL}/register/${inviteCode}` : '';

  return (
    <div className="invite-form-container">
      {introMessage || <p>Bring on a friend and share your expertise together!</p>}

      {!inviteCode ? (
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <TextField
              id="nickname"
              label="A nickname to personalize this invite"
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              aria-required="true"
              required
              fullWidth
              variant="outlined"
            />
          </Box>
          <div role="group" aria-labelledby="following-topics-label" className="form-group">
            <label id="following-topics-label">
              (Optional) Topics you want to follow your friend on:
            </label>
            <TopicSelector
              instanceId="invite-following-topic-selector"
              onChangeFn={(topic: DisplayConceptRef | null) => handleTopicSelect(topic, true)}
              aria-label="Select topics you want to follow your friend on"
            />
            <List dense aria-label="Selected topics to follow your friend on" sx={{ mt: 1 }}>
              {inviterTopics.map((topic, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <RemoveTopicButton
                      topic={topic.display}
                      onRemove={() => removeTopic(index, true)}
                    />
                  }
                  sx={{ pl: 0 }}
                >
                  <ListItemText primary={topic.display} />
                </ListItem>
              ))}
            </List>
          </div>
          <div role="group" aria-labelledby="followed-topics-label" className="form-group">
            <label id="followed-topics-label">
              (Optional) Topics your friend might want to follow you on:
            </label>
            <TopicSelector
              instanceId="invite-followed-topic-selector"
              onChangeFn={(topic: DisplayConceptRef | null) => handleTopicSelect(topic, false)}
              aria-label="Select topics your friend might want to follow you on"
            />
            <List
              dense
              aria-label="Selected topics for your friend to follow you on"
              sx={{ mt: 1 }}
            >
              {inviteeTopics.map((topic, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <RemoveTopicButton
                      topic={topic.display}
                      onRemove={() => removeTopic(index, false)}
                    />
                  }
                  sx={{ pl: 0 }}
                >
                  <ListItemText primary={topic.display} />
                </ListItem>
              ))}
            </List>
          </div>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            {onCancel && (
              <Button type="button" onClick={onCancel} variant="outlined">
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting || !nickname} variant="contained">
              {isSubmitting ? 'Creating...' : 'Create Invite'}
            </Button>
          </Box>
        </form>
      ) : (
        <section className="invite-code" aria-live="polite">
          <h3>Your Invite Code:</h3>
          <p className="invite-code-text">{inviteCode}</p>
          <p>Or use this link:</p>
          <p>
            <code className="invite-url">
              <a href={inviteUrl} target="_blank" rel="noopener noreferrer">
                {inviteUrl}
              </a>
            </code>
          </p>
          <p>After your friend signs up, they&apos;ll be directed to your post.</p>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(inviteUrl);
              }}
              variant="outlined"
            >
              Copy Link
            </Button>
            {onCancel && (
              <Button onClick={onCancel} variant="contained">
                Done
              </Button>
            )}
          </Box>
        </section>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </div>
  );
};
